import { InputCoverageCallout } from '@/components/Experiments/InputCoverageCallout';
import { requireOrdinaryEntityType } from '@einhaus/project314-shared';
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { inferRouterOutputs } from '@trpc/server';
import { useParams } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import UserLayout from '@/components/Layout/UserLayout';
import {
    Badge,
    Button,
    Card,
    ConfirmationDialog,
    DataTable,
    EmptyState,
    FormCheckbox,
    FormSelect,
    StatusBadge,
    Tabs,
    type Tab
} from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import TradingProfileSelect from '@/components/UI/TradingProfileSelect';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { useAppQuery } from '@/hooks/useAppQuery';
import type { AppRouter } from '@/types/appRouter';
import {
    type AeroCandidateReviewRow,
    formatAeroNumber,
    formatAeroPercent,
    formatAeroWinRatePercent
} from '@/features/tradeLogicAuthoring/components/aeroCandidateResultDetails';
import {
    formatAeroCandidatePersistenceLabel,
    getAeroCandidateEligibilityPresentation,
    getAeroRecoverableDataGapRatePercent,
    isAeroCandidatePromotable,
    isAeroCandidateVisibleByDefault
} from '@/features/tradeLogicAuthoring/components/aeroCandidatePromotionEligibility';
import ExitConfigChips from '@/features/tradeLogicAuthoring/components/ExitConfigChips';
import {
    AERO_PHASE_STEPS,
    formatAeroJobPhase,
    formatAeroJobTimestamp,
    getAeroJobExitConfigsScored,
    getAeroJobProgressPercent,
    getAeroJobSimulationProgressPercent,
    getAeroJobWorkUnitProgressPercent,
    getAeroPhaseIndex,
    summarizeAeroJobEntities
} from '@/features/tradeLogicAuthoring/components/aeroOptimizationJobs';
import {
    useAeroCandidateAggregatedResults,
    useAeroCandidateTopResults,
    useAeroOptionBuyingStrategyLeaderboard,
    useTradeLogicAuthoringBackendStatus,
    type AeroOptimizationJobDetailResult
} from '@/features/tradeLogicAuthoring/api';
import { type AeroObsPalette, buildAeroObsKey, buildAeroObsPalette, getAeroObsTone } from './aeroObsPalette';
import { type AeroResultFilterKey, type AeroResultFilters, createEmptyAeroResultFilters, filterAeroResults } from './aeroResultFilters';
import {
    formatAeroCompactNumber,
    formatAeroJobDisplayName,
    formatAeroShortHash,
    formatAeroStatus,
    getAeroStatusTone,
    isAeroJobActive,
    parsePositiveInt,
    usesLegacyAeroPortfolioMetrics
} from './aeroPageUtils';
import AeroObsFilter from './components/AeroObsFilter';
import AeroObsLeaderboard from './components/AeroObsLeaderboard';
import AeroResultsCharts from './components/AeroResultsCharts';
import AeroResultsFiltersControl from './components/AeroResultsFiltersControl';
import AeroUseResultPanel from './components/AeroUseResultPanel';
import CandidateComparison from './components/CandidateComparison';

type AeroOptimizationJobDetail = NonNullable<AeroOptimizationJobDetailResult['job']>;
type AeroResultSortBy = 'selection' | 'score' | 'avgProfit' | 'winsorizedScore' | 'winsorizedAvgProfit';
type AeroResultScope = 'top25' | 'top100' | 'all';
type RouterOutputs = inferRouterOutputs<AppRouter>;
type AeroExitSelectionBinding = RouterOutputs['exitOptimization']['fetchExitSelectionBindings']['bindings'][number];
type AeroExitSelectionDecision = RouterOutputs['exitOptimization']['fetchExitSelectionDecisions']['decisions'][number];
type AeroPointInTimeSnapshotSummary = RouterOutputs['exitOptimization']['fetchPointInTimeSnapshotSummary']['snapshotSummary'][number];
type AeroExitSelectionMode = AeroExitSelectionBinding['selectionMode'];
type AeroExitSelectionModeOption = { label: string; value: AeroExitSelectionMode; disabled: boolean };

const SCOPE_LIMITS: Record<AeroResultScope, number> = { top25: 25, top100: 100, all: 5000 };

const AeroDetails = () => {
    const { jobId } = useParams();
    const queryClient = useQueryClient();
    const parsedJobId = parsePositiveInt(jobId ?? null);

    const [sortBy, setSortBy] = useState<AeroResultSortBy>('selection');
    const [scope, setScope] = useState<AeroResultScope>('top100');
    const [selectionState, setSelectionState] = useState<{ jobId: number | null; hashes: string[] }>({ jobId: null, hashes: [] });
    const [isComparisonOpen, setIsComparisonOpen] = useState(false);
    const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
    const [entityFilter, setEntityFilter] = useState<string | null>(null);
    const [obsKeyFilter, setObsKeyFilter] = useState<string | null>(null);
    const [includeIneligibleResults, setIncludeIneligibleResults] = useState(false);
    const [resultFilters, setResultFilters] = useState<AeroResultFilters>(createEmptyAeroResultFilters);

    const selectedHashes = selectionState.jobId === parsedJobId ? selectionState.hashes : [];

    const jobQuery = useAppQuery({
        ...trpc.exitOptimization.fetchJob.queryOptions({ jobId: parsedJobId ?? 0 }),
        enabled: parsedJobId !== null,
        refetchInterval: (query) => (query.state.data?.job && isAeroJobActive(query.state.data.job) ? 5000 : false),
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const job = jobQuery.data?.job ?? null;
    const inputCoverage = jobQuery.data?.inputCoverage;
    const isCompleted = job?.status === 'completed';

    const selectedEntity =
        entityFilter === null
            ? null
            : (job?.entitiesDetailed?.find((entity) => `${entity.entityType}:${entity.entityId}` === entityFilter) ?? null);

    const resultsQuery = useAeroCandidateAggregatedResults({
        enabled: isCompleted && parsedJobId !== null,
        input: isCompleted && parsedJobId !== null ? { jobId: parsedJobId, limit: SCOPE_LIMITS[scope], sortBy } : null
    });

    const cancelJobMutation = useMutation({
        ...trpc.exitOptimization.cancelJob.mutationOptions(),
        onSuccess: async (result) => {
            setIsCancelDialogOpen(false);

            if (result.success) {
                showToast('AERO job cancellation requested', 'success');
                await queryClient.invalidateQueries(trpc.exitOptimization.fetchJob.pathFilter());
                await queryClient.invalidateQueries(trpc.exitOptimization.fetchJobs.pathFilter());
                return;
            }

            showToast(result.errorMessages.join(' ') || 'AERO job could not be cancelled', 'error');
        },
        onError: (error) => {
            setIsCancelDialogOpen(false);
            showToast(error instanceof Error ? error.message : 'AERO job could not be cancelled', 'error');
        }
    });

    const entityResultsQuery = useAeroCandidateTopResults({
        enabled: isCompleted && parsedJobId !== null && selectedEntity !== null,
        input:
            isCompleted && parsedJobId !== null && selectedEntity !== null
                ? {
                      jobId: parsedJobId,
                      limit: Math.min(SCOPE_LIMITS[scope], 100),
                      entityId: selectedEntity.entityId,
                      entityType: requireOrdinaryEntityType(selectedEntity.entityType)
                  }
                : null
    });

    const activeResultsQuery = selectedEntity !== null ? entityResultsQuery : resultsQuery;
    const rawResults = useMemo<AeroCandidateReviewRow[]>(() => activeResultsQuery.data?.results ?? [], [activeResultsQuery.data]);

    // Ranked server-side over every candidate per axis; the results table above is paged, so
    // deriving per-axis winners from it would compare axes on unequal samples.
    const leaderboardQuery = useAeroOptionBuyingStrategyLeaderboard({
        enabled: isCompleted,
        jobId: parsedJobId,
        sortBy
    });
    const obsLeaders = useMemo(() => leaderboardQuery.data?.leaders ?? [], [leaderboardQuery.data]);

    const obsPalette = useMemo<AeroObsPalette>(
        () =>
            buildAeroObsPalette({
                optionBuyingStrategies: job?.optionBuyingStrategies ?? [],
                optionBuyingStrategyIdsInResults: rawResults.map((result) => result.optionBuyingStrategyId)
            }),
        [job, rawResults]
    );

    const obsFilteredResults = useMemo(() => {
        if (obsKeyFilter === null) return rawResults;

        return rawResults.filter((candidate) => buildAeroObsKey(candidate.optionBuyingStrategyId) === obsKeyFilter);
    }, [rawResults, obsKeyFilter]);

    const defaultVisibleResults = useMemo(
        () =>
            obsFilteredResults.filter((candidate) =>
                isAeroCandidateVisibleByDefault(candidate, selectedEntity === null ? 'portfolio' : 'entity')
            ),
        [obsFilteredResults, selectedEntity]
    );
    const selectionSafeResultCount = defaultVisibleResults.length;
    const eligibilityFilteredResults = useMemo(
        () => (includeIneligibleResults ? obsFilteredResults : defaultVisibleResults),
        [includeIneligibleResults, obsFilteredResults, defaultVisibleResults]
    );
    const filteredResults = useMemo(
        () => filterAeroResults(eligibilityFilteredResults, resultFilters),
        [eligibilityFilteredResults, resultFilters]
    );
    const resultErrorMessages = activeResultsQuery.data?.errorMessages ?? [];

    // Leaderboard winners are ranked across the whole job, so an axis winner can sit outside the
    // current result page. Resolve selections against both sources or "Use this result" would
    // silently select nothing.
    const selectedCandidates = useMemo(() => {
        const candidatesByHash = new Map<string, AeroCandidateReviewRow>();

        for (const candidate of rawResults) candidatesByHash.set(candidate.variantHash, candidate);
        for (const leader of obsLeaders) {
            if (!candidatesByHash.has(leader.result.variantHash)) candidatesByHash.set(leader.result.variantHash, leader.result);
        }

        return selectedHashes
            .map((hash) => candidatesByHash.get(hash))
            .filter((candidate): candidate is AeroCandidateReviewRow => candidate !== undefined);
    }, [rawResults, obsLeaders, selectedHashes]);

    const backendStatus = useTradeLogicAuthoringBackendStatus({
        draftKind: 'exit',
        registryManifestFingerprint: null,
        strategyId: job?.strategyId ?? null
    });

    const promotionReady = backendStatus.capabilities.promoteCandidate.availability === 'available';

    const updateSelectedHashes = (hashes: string[]) => {
        setSelectionState({ jobId: parsedJobId, hashes });
    };

    const updateResultFilter = (key: AeroResultFilterKey, value: string) => {
        setResultFilters((currentFilters) => ({ ...currentFilters, [key]: value }));
    };

    if (parsedJobId === null) {
        return (
            <UserLayout containerSize="1400px" title="AERO Job - Project 314">
                <Card>
                    <div className="text-sm text-amber-100">Invalid AERO job ID.</div>
                </Card>
            </UserLayout>
        );
    }

    if (jobQuery.isLoading) {
        return (
            <UserLayout containerSize="1400px" title="AERO Job - Project 314">
                <Card>
                    <DataLoadingSpinner text="Loading AERO job..." />
                </Card>
            </UserLayout>
        );
    }

    if (jobQuery.error instanceof Error) {
        return (
            <UserLayout containerSize="1400px" title="AERO Job - Project 314">
                <Card>
                    <div className="rounded-md border border-red-900/60 bg-red-950/40 p-3 text-sm text-red-100">
                        {jobQuery.error.message}
                    </div>
                </Card>
            </UserLayout>
        );
    }

    if (job === null) {
        return (
            <UserLayout containerSize="1400px" title="AERO Job - Project 314">
                <Card>
                    <div className="text-sm text-zinc-400">AERO job #{parsedJobId} was not found.</div>
                </Card>
            </UserLayout>
        );
    }

    const canCancel = isAeroJobActive(job) && job.status !== 'cancelling';

    const entityOptions =
        (job.entitiesDetailed?.length ?? 0) >= 2
            ? (job.entitiesDetailed ?? []).map((entity) => ({
                  label: entity.entitySymbol,
                  value: `${entity.entityType}:${entity.entityId}`
              }))
            : [];

    return (
        <UserLayout containerSize="1400px" title={`${formatAeroJobDisplayName(job)} - Project 314`}>
            <AeroDetailsHeader
                canCancel={canCancel}
                job={job}
                onRefresh={() => jobQuery.refetch()}
                onRequestCancel={() => setIsCancelDialogOpen(true)}
            />

            {inputCoverage && <InputCoverageCallout coverage={inputCoverage} />}

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
                <div className="space-y-4">
                    <AeroJobProgressCard job={job} />

                    {isCompleted &&
                        selectedEntity === null &&
                        (obsPalette.hasMultipleAxes ? (
                            <AeroObsLeaderboard
                                activeObsKey={obsKeyFilter}
                                isLoading={leaderboardQuery.isLoading}
                                leaders={obsLeaders}
                                palette={obsPalette}
                                sortBy={sortBy}
                                onSelectObs={setObsKeyFilter}
                                onUseResult={(variantHash) => updateSelectedHashes([variantHash])}
                            />
                        ) : (
                            filteredResults.length > 0 && (
                                <AeroWinnerCard
                                    candidate={filteredResults[0]}
                                    sortBy={sortBy}
                                    onSelect={() => updateSelectedHashes([filteredResults[0].variantHash])}
                                />
                            )
                        ))}

                    <AeroResultsCard
                        entityFilter={entityFilter}
                        entityOptions={entityOptions}
                        filters={resultFilters}
                        includeIneligibleResults={includeIneligibleResults}
                        isInitialLoading={activeResultsQuery.isLoading}
                        isRefreshing={activeResultsQuery.isFetching && !activeResultsQuery.isLoading}
                        job={job}
                        obsFilterKey={obsKeyFilter}
                        obsPalette={obsPalette}
                        obsScopedResultCount={obsFilteredResults.length}
                        rawResults={rawResults}
                        resultErrorMessages={resultErrorMessages}
                        results={filteredResults}
                        scope={scope}
                        selectionSafeResultCount={selectionSafeResultCount}
                        selectedHashes={selectedHashes}
                        sortBy={sortBy}
                        totalResultCount={rawResults.length}
                        onCompare={() => setIsComparisonOpen(true)}
                        onEntityFilterChange={setEntityFilter}
                        onFilterChange={updateResultFilter}
                        onFiltersClear={() => setResultFilters(createEmptyAeroResultFilters())}
                        onFiltersReplace={setResultFilters}
                        onIncludeIneligibleResultsChange={setIncludeIneligibleResults}
                        onObsFilterChange={setObsKeyFilter}
                        onScopeChange={setScope}
                        onSelectedHashesChange={updateSelectedHashes}
                        onSortByChange={setSortBy}
                    />
                </div>

                {/* Ordered by the dependency chain: choose a result, then route it at runtime, then reference detail. */}
                <div className="space-y-4">
                    {isCompleted && (
                        <AeroUseResultPanel
                            backendReady={promotionReady}
                            jobId={job.id}
                            jobStrategyId={job.strategyId}
                            palette={obsPalette}
                            selectedCandidates={selectedCandidates}
                            onClearSelection={() => updateSelectedHashes([])}
                        />
                    )}
                    <AeroExitSelectionCard job={job} />
                    <AeroJobSidebarCard job={job} />
                </div>
            </div>

            <CandidateComparison
                candidates={selectedCandidates}
                isOpen={isComparisonOpen}
                palette={obsPalette}
                onClose={() => setIsComparisonOpen(false)}
            />

            <ConfirmationDialog
                confirmText="Cancel Job"
                confirmVariant="danger"
                isConfirmLoading={cancelJobMutation.isPending}
                isOpen={isCancelDialogOpen}
                message={`Stop AERO job ${formatAeroJobDisplayName(job)}? Work already completed is kept, but the run will not finish.`}
                title="Cancel AERO Job"
                onCancel={() => setIsCancelDialogOpen(false)}
                onConfirm={() => cancelJobMutation.mutate({ jobId: job.id })}
            />
        </UserLayout>
    );
};

/* -------------------------------------------------------------------------- */
/* Header                                                                     */
/* -------------------------------------------------------------------------- */

const AeroDetailsHeader = ({
    canCancel,
    job,
    onRefresh,
    onRequestCancel
}: {
    canCancel: boolean;
    job: AeroOptimizationJobDetail;
    onRefresh: () => void;
    onRequestCancel: () => void;
}) => (
    <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold text-zinc-100">{formatAeroJobDisplayName(job)}</h1>
                <StatusBadge status={job.status} tone={getAeroStatusTone(job.status)}>
                    {formatAeroStatus(job.status)}
                </StatusBadge>
            </div>
            <p className="text-sm text-zinc-400">
                {job.strategyName} #{job.strategyId} • {summarizeAeroJobEntities(job)}
            </p>
        </div>
        <div className="flex flex-wrap gap-2">
            <Button linkProps={{ reloadDocument: true }} size="sm" to="/user/aero" variant="outline-dark">
                Job List
            </Button>
            <Button size="sm" to={`/user/aero/new?cloneFrom=${job.id}`} variant="outline-dark">
                Duplicate
            </Button>
            <Button size="sm" variant="outline-dark" onClick={onRefresh}>
                Refresh
            </Button>
            {canCancel && (
                <Button size="sm" variant="destructive" onClick={onRequestCancel}>
                    Cancel Job
                </Button>
            )}
            <Button size="sm" to={`/user/tradingStrategy/${job.strategyId}`}>
                Strategy
            </Button>
        </div>
    </div>
);

/* -------------------------------------------------------------------------- */
/* Progress                                                                   */
/* -------------------------------------------------------------------------- */

const formatAeroJobDuration = (job: AeroOptimizationJobDetail): string | null => {
    if (!job.startedAt) return null;

    const start = new Date(job.startedAt).getTime();
    const end = job.completedAt ? new Date(job.completedAt).getTime() : Date.now();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;

    const totalSeconds = Math.floor((end - start) / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;

    return `${seconds}s`;
};

const AeroJobProgressCard = ({ job }: { job: AeroOptimizationJobDetail }) => {
    const isTerminal = job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled';
    const [isExpanded, setIsExpanded] = useState(!isTerminal);

    const progressPercent = job.totalWorkUnits > 0 ? getAeroJobWorkUnitProgressPercent(job) : getAeroJobProgressPercent(job);
    const entitySimulationProgressPercent = getAeroJobSimulationProgressPercent(job);

    const duration = formatAeroJobDuration(job);
    const exitConfigsScored = getAeroJobExitConfigsScored(job);

    if (isTerminal && !isExpanded) {
        const summary = [
            duration
                ? `${job.status === 'completed' ? 'Completed' : job.status === 'failed' ? 'Failed' : 'Cancelled'} in ${duration}`
                : null,
            `${formatAeroCompactNumber(exitConfigsScored)} exit configs scored`,
            `${formatAeroCompactNumber(job.entityCombinationSimulationsProcessed)} / ${formatAeroCompactNumber(
                job.totalEntityCombinationSimulations
            )} entity sims`
        ]
            .filter(Boolean)
            .join(' • ');

        return (
            <Card
                headerActions={
                    <Button size="sm" variant="outline-dark" onClick={() => setIsExpanded(true)}>
                        Show details
                    </Button>
                }
                title="Job Progress"
            >
                <div className="text-sm text-zinc-300">{summary}</div>
                {job.errorMessage && (
                    <div className="mt-3 rounded-md border border-red-900/60 bg-red-950/40 p-3 text-sm text-red-100">
                        {job.errorMessage}
                    </div>
                )}
            </Card>
        );
    }

    return (
        <Card
            headerActions={
                isTerminal ? (
                    <Button size="sm" variant="outline-dark" onClick={() => setIsExpanded(false)}>
                        Collapse
                    </Button>
                ) : undefined
            }
            subtitle={job.description ?? 'No description'}
            title="Job Progress"
        >
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
                <Metric label="Phase" value={formatAeroJobPhase(job.currentPhase)} />
                <Metric
                    label="Work units"
                    value={`${formatAeroCompactNumber(job.completedWorkUnits)} / ${formatAeroCompactNumber(job.totalWorkUnits)}`}
                />
                <Metric
                    label="Exit configs scored"
                    value={`${formatAeroCompactNumber(exitConfigsScored)} / ${formatAeroCompactNumber(job.totalCombinations)}`}
                />
                <Metric
                    label="Entity simulations"
                    value={`${formatAeroCompactNumber(job.entityCombinationSimulationsProcessed)} / ${formatAeroCompactNumber(
                        job.totalEntityCombinationSimulations
                    )} (${entitySimulationProgressPercent}%)`}
                />
                <Metric
                    label="Tradable entities"
                    value={`${formatAeroCompactNumber(job.tradableEntityCount)} / ${formatAeroCompactNumber(job.submittedEntityCount)}`}
                />
                <Metric label={isTerminal ? 'Duration' : 'Started'} value={duration ?? formatAeroJobTimestamp(job.startedAt)} />
            </div>

            {!isTerminal && (
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {AERO_PHASE_STEPS.map((step, index) => {
                        const currentIndex = getAeroPhaseIndex(job.currentPhase);
                        const done = currentIndex > index;
                        const active = currentIndex === index;

                        const toneClass = done
                            ? 'border-green-800 bg-green-900/30 text-green-300'
                            : active
                              ? 'border-cyan-700 bg-cyan-900/30 text-cyan-200'
                              : 'border-zinc-800 bg-black/20 text-zinc-500';

                        return (
                            <div className={`rounded-md border px-3 py-2 ${toneClass}`} key={step.key}>
                                <div className="text-sm font-medium">{step.label}</div>
                                <div className="text-xs">{done ? 'Done' : active ? 'In progress' : 'Pending'}</div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="mt-4">
                <div className="mb-1 flex items-center justify-between text-xs text-zinc-500">
                    <span>Overall progress</span>
                    <span>{progressPercent}%</span>
                </div>
                <div className="h-2 rounded bg-zinc-800">
                    <div
                        className={`h-2 rounded ${job.status === 'failed' ? 'bg-red-600' : 'bg-cyan-600'}`}
                        style={{ width: `${progressPercent}%` }}
                    />
                </div>
            </div>

            {job.errorMessage && (
                <div className="mt-4 rounded-md border border-red-900/60 bg-red-950/40 p-3 text-sm text-red-100">{job.errorMessage}</div>
            )}
        </Card>
    );
};

/* -------------------------------------------------------------------------- */
/* Winner                                                                     */
/* -------------------------------------------------------------------------- */

const AeroWinnerCard = ({
    candidate,
    sortBy,
    onSelect
}: {
    candidate: AeroCandidateReviewRow;
    sortBy: AeroResultSortBy;
    onSelect: () => void;
}) => (
    <Card
        className="border-cyan-900/70"
        headerActions={
            <Button disabled={!isAeroCandidatePromotable(candidate)} size="sm" onClick={onSelect}>
                Use this result
            </Button>
        }
        subtitle={`Highest-ranked result in the current view using ${sortBy === 'selection' ? 'the experiment selection policy' : sortBy}.`}
        title="Top Result"
    >
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Metric label="Selected score" value={formatAeroNumber(candidate.selectedLiveScore)} />
            <Metric label="Raw avg profit" value={formatAeroPercent(candidate.avgProfit)} />
            <Metric label="Winsorized avg" value={formatAeroPercent(candidate.winsorizedAvgProfit)} />
            <Metric label="Win rate" value={formatAeroWinRatePercent(candidate.winRate)} />
            <Metric label="Profit factor" value={formatAeroNumber(candidate.profitFactor)} />
        </div>
        <div className="mt-3">
            <div className="mb-1 text-xs uppercase tracking-wide text-zinc-500">Exit configuration</div>
            <ExitConfigChips candidate={candidate} />
        </div>
    </Card>
);

/* -------------------------------------------------------------------------- */
/* Results                                                                    */
/* -------------------------------------------------------------------------- */

const AeroResultsCard = ({
    entityFilter,
    entityOptions,
    filters,
    includeIneligibleResults,
    isInitialLoading,
    isRefreshing,
    job,
    obsFilterKey,
    obsPalette,
    obsScopedResultCount,
    rawResults,
    resultErrorMessages,
    results,
    scope,
    selectionSafeResultCount,
    selectedHashes,
    sortBy,
    totalResultCount,
    onCompare,
    onEntityFilterChange,
    onFilterChange,
    onFiltersClear,
    onFiltersReplace,
    onIncludeIneligibleResultsChange,
    onObsFilterChange,
    onScopeChange,
    onSelectedHashesChange,
    onSortByChange
}: {
    entityFilter: string | null;
    entityOptions: { label: string; value: string }[];
    filters: AeroResultFilters;
    includeIneligibleResults: boolean;
    isInitialLoading: boolean;
    isRefreshing: boolean;
    job: AeroOptimizationJobDetail;
    obsFilterKey: string | null;
    obsPalette: AeroObsPalette;
    obsScopedResultCount: number;
    rawResults: AeroCandidateReviewRow[];
    resultErrorMessages: string[];
    results: AeroCandidateReviewRow[];
    scope: AeroResultScope;
    selectionSafeResultCount: number;
    selectedHashes: string[];
    sortBy: AeroResultSortBy;
    totalResultCount: number;
    onCompare: () => void;
    onEntityFilterChange: (entityKey: string | null) => void;
    onFilterChange: (key: AeroResultFilterKey, value: string) => void;
    onFiltersClear: () => void;
    onFiltersReplace: (filters: AeroResultFilters) => void;
    onIncludeIneligibleResultsChange: (include: boolean) => void;
    onObsFilterChange: (obsKey: string | null) => void;
    onScopeChange: (scope: AeroResultScope) => void;
    onSelectedHashesChange: (hashes: string[]) => void;
    onSortByChange: (sortBy: AeroResultSortBy) => void;
}) => {
    const hasActiveFilters = results.length !== rawResults.length || obsFilterKey !== null;
    const hiddenIneligibleResultCount = obsScopedResultCount - selectionSafeResultCount;
    const emptyMessage =
        !includeIneligibleResults && selectionSafeResultCount === 0 && obsScopedResultCount > 0
            ? 'No selection-safe results are available. Enable Include ineligible diagnostics to inspect the rejected rows.'
            : hasActiveFilters
              ? 'No results match the active filters.'
              : 'No result rows are available.';

    const tabs = useMemo<Tab[]>(
        () => [
            {
                id: 'results',
                label: 'Results',
                content: (
                    <AeroResultsTable
                        emptyMessage={emptyMessage}
                        isInitialLoading={isInitialLoading}
                        isRefreshing={isRefreshing}
                        palette={obsPalette}
                        results={results}
                        selectionEnabled={entityFilter === null}
                        selectedHashes={selectedHashes}
                        onCompare={onCompare}
                        onSelectedHashesChange={onSelectedHashesChange}
                    />
                )
            },
            {
                id: 'charts',
                label: 'Charts',
                disabled: results.length === 0,
                content: <AeroResultsCharts palette={obsPalette} results={results} />
            }
        ],
        [emptyMessage, entityFilter, isInitialLoading, isRefreshing, obsPalette, results, selectedHashes, onCompare, onSelectedHashesChange]
    );

    return (
        <Card
            headerActions={
                job.status === 'completed' ? (
                    <StatusBadge status="ready">Results ready</StatusBadge>
                ) : (
                    <StatusBadge status="pending" tone="warning">
                        Waiting for completion
                    </StatusBadge>
                )
            }
            subtitle="Review candidates in the selected order, compare them, and pick one to apply to a strategy."
            title="Results"
        >
            {job.status !== 'completed' ? (
                <div className="rounded-md border border-zinc-800 bg-black/20 p-4 text-sm text-zinc-400">
                    Results appear here once the AERO job completes.
                </div>
            ) : (
                <>
                    {usesLegacyAeroPortfolioMetrics(job) && (
                        <div className="mb-3 rounded-md border border-amber-800/70 bg-amber-950/30 p-3 text-sm text-amber-100" role="alert">
                            This job predates AERO&apos;s exact portfolio-metric fix. Treat Sharpe ratio, drawdown, composite score, and
                            score-based ordering as legacy; rerun the job before promoting a result.
                        </div>
                    )}

                    {entityFilter === null && (
                        <div className="mb-3 rounded-md border border-zinc-800 bg-black/20 p-2.5 text-xs text-zinc-400">
                            Canonical rank follows this job&apos;s frozen option-return policy:{' '}
                            <span className="font-medium text-zinc-200">
                                {job.optionReturnSelectionPolicy === 'upper_winsorized_v1' ? 'winsorized returns' : 'raw returns'}
                            </span>
                            . Both result families remain visible below. Winsorization caps only the upper tail and never rewrites stored
                            raw trades.
                        </div>
                    )}

                    <div className={`mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 ${entityOptions.length > 0 ? 'lg:grid-cols-3' : ''}`}>
                        {entityOptions.length > 0 && (
                            <FormSelect
                                noMargin
                                label="Entity"
                                options={[{ label: 'All entities (portfolio)', value: '' }, ...entityOptions]}
                                value={entityFilter === null ? '' : String(entityFilter)}
                                onChange={(event) => onEntityFilterChange(event.target.value || null)}
                            />
                        )}
                        {entityFilter === null && (
                            <FormSelect
                                noMargin
                                label="Rank by"
                                options={[
                                    { label: 'Experiment selection policy (default)', value: 'selection' },
                                    { label: 'Raw average profit', value: 'avgProfit' },
                                    { label: 'Winsorized average profit', value: 'winsorizedAvgProfit' },
                                    { label: 'Raw composite score', value: 'score' },
                                    { label: 'Winsorized composite score', value: 'winsorizedScore' }
                                ]}
                                value={sortBy}
                                onChange={(event) => onSortByChange(event.target.value as AeroResultSortBy)}
                            />
                        )}
                        <FormSelect
                            noMargin
                            label="Show"
                            options={[
                                { label: 'Top 25', value: 'top25' },
                                { label: 'Top 100', value: 'top100' },
                                { label: 'All (up to 5000)', value: 'all' }
                            ]}
                            value={scope}
                            onChange={(event) => onScopeChange(event.target.value as AeroResultScope)}
                        />
                    </div>

                    {entityFilter !== null && (
                        <div className="mb-3 rounded-md border border-zinc-800 bg-black/20 p-2.5 text-xs text-zinc-400">
                            Showing per-entity results. Composite scores are available in the portfolio view. Missing equity-curve drawdown
                            is shown as unavailable. Apply results from the portfolio view.
                        </div>
                    )}

                    <AeroObsFilter activeObsKey={obsFilterKey} palette={obsPalette} results={rawResults} onChange={onObsFilterChange} />

                    <div className="mb-3 rounded-md border border-zinc-800 bg-black/20 p-2.5">
                        <FormCheckbox
                            checked={includeIneligibleResults}
                            helpText="Rejected rows remain available for diagnosis but cannot be selected or applied. Current runs require complete post-entry outcomes: unresolved data gaps, unsupported outcomes, and engine failures block selection. Older runs keep their recorded eligibility."
                            id="aero-include-ineligible-results"
                            label={`Include ineligible diagnostics${hiddenIneligibleResultCount > 0 ? ` (${hiddenIneligibleResultCount})` : ''}`}
                            labelClassName="text-zinc-300"
                            onChange={(event) => onIncludeIneligibleResultsChange(event.target.checked)}
                        />
                    </div>

                    <AeroResultsFiltersControl
                        filters={filters}
                        resultCount={results.length}
                        totalResultCount={
                            includeIneligibleResults
                                ? obsFilterKey === null
                                    ? totalResultCount
                                    : obsScopedResultCount
                                : selectionSafeResultCount
                        }
                        onChange={onFilterChange}
                        onClear={onFiltersClear}
                        onReplace={onFiltersReplace}
                    />

                    {resultErrorMessages.length > 0 && (
                        <div className="mb-3 rounded-md border border-red-900/60 bg-red-950/40 p-3 text-sm text-red-100">
                            {resultErrorMessages.join(' ')}
                        </div>
                    )}

                    <Tabs tabs={tabs} />
                </>
            )}
        </Card>
    );
};

const AeroResultsTable = ({
    emptyMessage,
    isInitialLoading,
    isRefreshing,
    palette,
    results,
    selectionEnabled,
    selectedHashes,
    onCompare,
    onSelectedHashesChange
}: {
    emptyMessage: string;
    isInitialLoading: boolean;
    isRefreshing: boolean;
    palette: AeroObsPalette;
    results: AeroCandidateReviewRow[];
    selectionEnabled: boolean;
    selectedHashes: string[];
    onCompare: () => void;
    onSelectedHashesChange: (hashes: string[]) => void;
}) => {
    const selectedHashSet = useMemo(() => new Set(selectedHashes), [selectedHashes]);

    const promotableHashes = useMemo(
        () => (selectionEnabled ? results.filter(isAeroCandidatePromotable).map((candidate) => candidate.variantHash) : []),
        [results, selectionEnabled]
    );

    const toggleHash = (variantHash: string) => {
        if (selectedHashSet.has(variantHash)) {
            onSelectedHashesChange(selectedHashes.filter((hash) => hash !== variantHash));
            return;
        }

        onSelectedHashesChange([...selectedHashes, variantHash]);
    };

    const columns = useMemo<ColumnDef<AeroCandidateReviewRow>[]>(
        () => [
            {
                id: 'select',
                header: '',
                cell: ({ row }) => {
                    const candidate = row.original;
                    const promotable = isAeroCandidatePromotable(candidate);

                    return (
                        <input
                            aria-label={`Select candidate ${formatAeroShortHash(candidate.variantHash)}`}
                            checked={selectedHashSet.has(candidate.variantHash)}
                            className="h-4 w-4 rounded border-zinc-600 bg-zinc-900 text-cyan-600"
                            disabled={!selectionEnabled || !promotable}
                            title={
                                selectionEnabled
                                    ? promotable
                                        ? 'Select this result'
                                        : 'This result is not eligible for selection'
                                    : 'Select results from the portfolio view'
                            }
                            type="checkbox"
                            onChange={() => toggleHash(candidate.variantHash)}
                        />
                    );
                }
            },
            {
                id: 'rank',
                header: '#',
                cell: ({ row }) => (
                    <div>
                        <div className="font-medium text-zinc-200">{row.index + 1}</div>
                        <div className="font-mono text-[10px] text-zinc-600">{formatAeroShortHash(row.original.variantHash)}</div>
                    </div>
                )
            },
            ...(palette.hasMultipleAxes
                ? [
                      {
                          id: 'obs',
                          header: 'Axis',
                          cell: ({ row }: { row: { original: AeroCandidateReviewRow } }) => {
                              const tone = getAeroObsTone(palette, row.original.optionBuyingStrategyId);

                              return (
                                  <span
                                      className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs font-medium ${tone.chipClassName}`}
                                      title={tone.label}
                                  >
                                      <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${tone.dotClassName}`} />
                                      <span className="truncate">{tone.shortLabel}</span>
                                  </span>
                              );
                          }
                      } as ColumnDef<AeroCandidateReviewRow>
                  ]
                : []),
            {
                id: 'eligibility',
                header: 'Eligibility',
                cell: ({ row }) => {
                    const candidate = row.original;
                    const eligibility = getAeroCandidateEligibilityPresentation(candidate, selectionEnabled ? 'portfolio' : 'entity');
                    const gapCount = candidate.recoverableDataGapEvaluationCount;
                    const gapRate = getAeroRecoverableDataGapRatePercent(candidate);

                    return (
                        <div className="space-y-1">
                            <Badge title={eligibility.detail} variant={eligibility.badgeVariant}>
                                {eligibility.label}
                            </Badge>
                            {gapCount !== null && gapCount !== undefined && (
                                <div className="text-[10px] text-zinc-500">
                                    {formatAeroCompactNumber(gapCount)} gap{gapCount === 1 ? '' : 's'} ·{' '}
                                    {gapRate === null ? 'rate unavailable' : `${formatAeroNumber(gapRate)}%`}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                id: 'exit',
                header: 'Exit configuration',
                cell: ({ row }) => (
                    <div className="space-y-1">
                        <ExitConfigChips candidate={row.original} />
                        {/* Provenance, not signal — kept quiet so the exit chips stay readable. */}
                        <div className="text-[10px] text-zinc-600">{formatAeroCandidatePersistenceLabel(row.original)}</div>
                    </div>
                )
            },
            {
                accessorKey: 'selectedLiveScore',
                header: 'Selected Score',
                cell: ({ row }) => (selectionEnabled ? formatAeroNumber(row.original.selectedLiveScore) : 'Not available')
            },
            { accessorKey: 'avgProfit', header: 'Raw Avg', cell: ({ row }) => formatAeroPercent(row.original.avgProfit) },
            {
                accessorKey: 'winsorizedAvgProfit',
                header: 'Winsorized Avg',
                cell: ({ row }) => formatAeroPercent(row.original.winsorizedAvgProfit)
            },
            {
                id: 'winsorization',
                header: 'Upper-tail cap',
                cell: ({ row }) =>
                    row.original.winsorizationCap === null ? (
                        'None'
                    ) : (
                        <div>
                            <div>{formatAeroPercent(row.original.winsorizationCap)}</div>
                            <div className="text-[10px] text-zinc-500">
                                {formatAeroCompactNumber(row.original.winsorizedTradeCount ?? 0)} capped
                            </div>
                        </div>
                    )
            },
            { accessorKey: 'winRate', header: 'Win Rate', cell: ({ row }) => formatAeroWinRatePercent(row.original.winRate) },
            { accessorKey: 'profitFactor', header: 'Profit Factor', cell: ({ row }) => formatAeroNumber(row.original.profitFactor) },
            {
                accessorKey: 'drawdown',
                header: 'Drawdown',
                cell: ({ row }) => (row.original.drawdown === null ? 'Not available' : formatAeroPercent(row.original.drawdown))
            },
            { accessorKey: 'totalTrades', header: 'Trades', cell: ({ row }) => formatAeroCompactNumber(row.original.totalTrades) }
        ],
        [palette, selectedHashSet, selectedHashes, selectionEnabled]
    );

    // Only unmount the table for the very first load. A background refetch (window refocus, or an
    // invalidation after publishing) must keep it mounted — DataTable holds search, column sort, and
    // the current page in local state, so swapping it for a spinner silently discards all three.
    if (isInitialLoading) {
        return <DataLoadingSpinner text="Loading AERO results..." />;
    }

    if (results.length === 0) {
        return <div className="rounded-md border border-zinc-800 bg-black/20 p-4 text-sm text-zinc-400">{emptyMessage}</div>;
    }

    return (
        <div className="relative">
            <div className="mb-3 flex flex-wrap items-center gap-2">
                {selectionEnabled ? (
                    <Button size="sm" variant="outline-dark" onClick={() => onSelectedHashesChange(promotableHashes)}>
                        Select eligible
                    </Button>
                ) : (
                    <span className="text-xs text-zinc-500">Return to the portfolio view to select a result.</span>
                )}
                {selectedHashes.length === 0 && (
                    <span className="text-xs text-zinc-500">Tick rows to compare them or apply one to a strategy.</span>
                )}
                {isRefreshing && <span className="text-xs text-zinc-500">Refreshing...</span>}
            </div>
            <DataTable search sort columns={columns} data={results} pagination={{ enabled: true, pageSize: 15 }} />

            {selectedHashes.length > 0 && (
                <div className="sticky bottom-3 z-20 mt-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-cyan-700/70 bg-zinc-900/95 px-3 py-2 shadow-lg backdrop-blur">
                    <div className="flex items-center gap-2">
                        <Badge variant="info">{selectedHashes.length} selected</Badge>
                        <span className="hidden text-xs text-zinc-400 sm:inline">
                            Compare them, or apply them from Use This Result on the right.
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline-dark" onClick={() => onSelectedHashesChange([])}>
                            Clear
                        </Button>
                        <Button disabled={selectedHashes.length < 2} size="sm" onClick={onCompare}>
                            Compare ({selectedHashes.length})
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

/* -------------------------------------------------------------------------- */
/* Config & errors                                                            */
/* -------------------------------------------------------------------------- */

const AeroJobConfigContent = ({ job }: { job: AeroOptimizationJobDetail }) => (
    <div className="space-y-2">
        <Metric label="Stop Loss" value={describeStopLoss(job)} />
        <Metric
            label="Maximum Hold Bars"
            value={
                job.config.maxHoldBars
                    ? `${job.config.maxHoldBars.bars.join(', ') || 'none'}${job.config.maxHoldBars.includeNoLimit ? ' + no limit' : ''}`
                    : '—'
            }
        />
        <Metric label="Trailing Stop" value={describeTrailing(job)} />
        <Metric label="Profit Target" value={formatPercentList(job.config.profitTarget?.percentages)} />
        <Metric label="Partial Exits" value={describePartialExits(job)} />
        <Metric label="Profit Floors" value={describeProfitFloors(job)} />
        <Metric label="Exit Price Fill" value={job.config.priceFillModel === 'resting_level' ? 'Resting level' : 'Observed close'} />
        <Metric label="Option Strategies" value={describeOptionStrategies(job)} />
        <Metric label="Max Hold Days" value={formatAeroCompactNumber(job.maxTradeDurationDays)} />
        <Metric label="Min Option Price" value={job.minOptionPrice === null ? 'No floor' : `$${job.minOptionPrice}`} />
        <Metric label="Backtest Window" value={describeWindow(job)} />
    </div>
);

const AeroUniverseGateContent = ({ job }: { job: AeroOptimizationJobDetail }) => (
    <div className="space-y-3">
        <div className="grid grid-cols-1 gap-2">
            <Metric label="Base universe" value={describeAeroBaseUniverse(job)} />
            <Metric label="Strategy screener" value={describeAeroEntryGate(job)} />
        </div>
        <div className="grid grid-cols-2 gap-2">
            <Metric label="Candidate entities" value={formatAeroCompactNumber(job.candidateUniverseCount)} />
            <Metric
                label="Upfront qualified"
                value={job.upfrontQualifiedCount > 0 ? formatAeroCompactNumber(job.upfrontQualifiedCount) : 'Not enabled'}
            />
            <Metric label="PIT raw entries" value={formatAeroCompactNumber(job.pitRawEntries)} />
            <Metric label="PIT qualified" value={formatAeroCompactNumber(job.pitQualifiedEntries)} />
            <Metric label="PIT rejected" value={formatAeroCompactNumber(job.pitRejectedEntries)} />
            <Metric label="Missing data" value={formatAeroCompactNumber(job.pitMissingDataEntries)} />
            <Metric label="Entities with entries" value={formatAeroCompactNumber(job.entitiesWithEntries)} />
            <Metric label="Option coverage" value={formatAeroCompactNumber(job.tradableEntityOptionAxes)} />
        </div>
    </div>
);

const AeroWorkUnitErrorsContent = ({ job }: { job: AeroOptimizationJobDetail }) => {
    if (job.workUnitErrors.length === 0) {
        return <EmptyState size="sm" title="No work unit errors were reported." />;
    }

    return (
        <div className="space-y-2">
            {job.workUnitErrors.map((error) => (
                <div className="rounded-md border border-red-900/60 bg-red-950/30 p-3" key={error.id}>
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="danger">{error.workType}</Badge>
                        <span className="text-xs text-zinc-500">Retries: {error.retryCount}</span>
                    </div>
                    <div className="mt-2 text-sm text-red-100">{error.errorMessage ?? 'Unknown work unit error'}</div>
                </div>
            ))}
        </div>
    );
};

const AeroJobSidebarCard = ({ job }: { job: AeroOptimizationJobDetail }) => {
    const errorCount = job.workUnitErrors.length;
    // Reference material, not a decision — collapsed unless something needs attention.
    const [isExpanded, setIsExpanded] = useState(errorCount > 0);

    const tabs = useMemo<Tab[]>(
        () => [
            { id: 'universe', label: 'Universe', content: <AeroUniverseGateContent job={job} /> },
            { id: 'config', label: 'Configuration', content: <AeroJobConfigContent job={job} /> },
            {
                id: 'errors',
                label: errorCount > 0 ? `Errors (${errorCount})` : 'Errors',
                content: <AeroWorkUnitErrorsContent job={job} />
            }
        ],
        [job, errorCount]
    );

    return (
        <Card
            headerActions={
                <Button size="sm" variant="outline-dark" onClick={() => setIsExpanded((expanded) => !expanded)}>
                    {isExpanded ? 'Hide' : 'Show'}
                </Button>
            }
            title="Job Details"
        >
            {isExpanded ? (
                <Tabs tabs={tabs} />
            ) : (
                <div className="text-sm text-zinc-400">
                    Universe, configuration, and {errorCount > 0 ? `${errorCount} work unit errors` : 'work unit errors'}.
                </div>
            )}
        </Card>
    );
};

const AeroExitSelectionCard = ({ job }: { job: AeroOptimizationJobDetail }) => {
    const queryClient = useQueryClient();
    const isCompleted = job.status === 'completed';

    const snapshotSummaryQuery = useAppQuery({
        ...trpc.exitOptimization.fetchPointInTimeSnapshotSummary.queryOptions({ jobId: job.id }),
        enabled: isCompleted,
        staleTime: 30000
    });

    const bindingsQuery = useAppQuery({
        ...trpc.exitOptimization.fetchExitSelectionBindings.queryOptions({
            sourceAeroJobId: job.id,
            includeRetired: true
        }),
        enabled: isCompleted,
        staleTime: 30000
    });

    const decisionsQuery = useAppQuery({
        ...trpc.exitOptimization.fetchExitSelectionDecisions.queryOptions({
            sourceAeroJobId: job.id,
            limit: 8
        }),
        enabled: isCompleted,
        staleTime: 30000
    });

    const refreshSelectionQueries = async () => {
        await Promise.all([
            queryClient.invalidateQueries(trpc.exitOptimization.fetchExitSelectionBindings.pathFilter()),
            queryClient.invalidateQueries(trpc.exitOptimization.fetchExitSelectionDecisions.pathFilter()),
            queryClient.invalidateQueries(trpc.exitOptimization.fetchPointInTimeSnapshotSummary.pathFilter())
        ]);
    };

    const createBindingMutation = useMutation({
        ...trpc.exitOptimization.createExitSelectionBinding.mutationOptions(),
        onSuccess: async (result) => {
            if (!result.success) {
                showToast(result.errorMessages.join(' ') || 'AERO exit selection binding could not be created', 'error');
                return;
            }

            showToast('AERO exit selection binding is active', 'success');
            await refreshSelectionQueries();
        },
        onError: (error) => {
            showToast(error instanceof Error ? error.message : 'AERO exit selection binding could not be created', 'error');
        }
    });

    const retireBindingMutation = useMutation({
        ...trpc.exitOptimization.retireExitSelectionBinding.mutationOptions(),
        onSuccess: async (result) => {
            if (!result.success) {
                showToast(result.errorMessages.join(' ') || 'AERO exit selection binding could not be retired', 'error');
                return;
            }

            showToast('AERO exit selection binding retired', 'success');
            await refreshSelectionQueries();
        },
        onError: (error) => {
            showToast(error instanceof Error ? error.message : 'AERO exit selection binding could not be retired', 'error');
        }
    });

    const snapshotSummary = snapshotSummaryQuery.data?.snapshotSummary ?? [];
    const bindings = bindingsQuery.data?.bindings ?? [];
    const decisions = decisionsQuery.data?.decisions ?? [];
    const activeBindingCount = bindings.filter((binding) => binding.status === 'active').length;

    return (
        <Card
            headerActions={
                activeBindingCount > 0 ? (
                    <StatusBadge status="active" tone="success">
                        Active
                    </StatusBadge>
                ) : (
                    <StatusBadge status={isCompleted ? 'ready' : 'pending'} tone={isCompleted ? 'warning' : 'neutral'}>
                        {isCompleted ? 'Unbound' : 'Waiting'}
                    </StatusBadge>
                )
            }
            subtitle="Route portfolio backtests and live trades to point-in-time AERO exit snapshots."
            title="PIT Exit Selection"
        >
            {!isCompleted ? (
                <div className="rounded-md border border-zinc-800 bg-black/20 p-3 text-sm text-zinc-400">
                    Complete the AERO job before creating a runtime binding.
                </div>
            ) : (
                <AeroExitSelectionCompletedContent
                    bindings={bindings}
                    decisions={decisions}
                    isCreating={createBindingMutation.isPending}
                    isDecisionLoading={decisionsQuery.isLoading}
                    isRetiring={retireBindingMutation.isPending}
                    snapshots={snapshotSummary}
                    onCreateBinding={(input) =>
                        createBindingMutation.mutate({
                            sourceAeroJobId: job.id,
                            strategyId: job.strategyId,
                            ...input
                        })
                    }
                    onRetireBinding={(bindingId) => retireBindingMutation.mutate({ bindingId })}
                />
            )}
        </Card>
    );
};

const AeroExitSelectionCompletedContent = ({
    bindings,
    decisions,
    isCreating,
    isDecisionLoading,
    isRetiring,
    snapshots,
    onCreateBinding,
    onRetireBinding
}: {
    bindings: AeroExitSelectionBinding[];
    decisions: AeroExitSelectionDecision[];
    isCreating: boolean;
    isDecisionLoading: boolean;
    isRetiring: boolean;
    snapshots: AeroPointInTimeSnapshotSummary[];
    onCreateBinding: (input: {
        tradingProfileId: number | null;
        selectionMode: AeroExitSelectionMode;
        requireOptionBuyingStrategyMatch: boolean;
        effectiveFromTimestamp: number;
    }) => void;
    onRetireBinding: (bindingId: number) => void;
}) => {
    const [selectedTradingProfileId, setSelectedTradingProfileId] = useState<number | null>(null);
    const [selectionMode, setSelectionMode] = useState<AeroExitSelectionMode>('entity_with_overall_fallback');
    const [requireOptionBuyingStrategyMatch, setRequireOptionBuyingStrategyMatch] = useState(true);

    const activeBindings = bindings.filter((binding) => binding.status === 'active');
    const retiredBindingCount = bindings.length - activeBindings.length;
    const hasOverallSnapshots = snapshots.some((summary) => summary.selectionScope === 'overall');
    const hasEntitySnapshots = snapshots.some((summary) => summary.selectionScope === 'entity');
    const selectedModeSupported = selectionMode === 'entity' ? hasEntitySnapshots : hasOverallSnapshots;

    const earliestSnapshotAvailableAtTimestamp = snapshots.reduce<number | null>((earliest, snapshot) => {
        if (snapshot.earliestAvailableAtTimestamp == null) return earliest;
        if (earliest == null) return snapshot.earliestAvailableAtTimestamp;
        return Math.min(earliest, snapshot.earliestAvailableAtTimestamp);
    }, null);

    const selectionModeOptions: AeroExitSelectionModeOption[] = [
        { label: 'Entity with overall fallback', value: 'entity_with_overall_fallback', disabled: !hasOverallSnapshots },
        { label: 'Overall winner', value: 'overall', disabled: !hasOverallSnapshots },
        { label: 'Entity only', value: 'entity', disabled: !hasEntitySnapshots }
    ];

    const createBinding = () => {
        const tradingProfileId = selectedTradingProfileId;

        onCreateBinding({
            tradingProfileId,
            selectionMode,
            requireOptionBuyingStrategyMatch,
            effectiveFromTimestamp: earliestSnapshotAvailableAtTimestamp ?? 0
        });
    };

    return (
        <div className="space-y-4">
            <AeroSnapshotReadiness snapshots={snapshots} />

            {activeBindings.length > 0 && (
                <div className="space-y-2">
                    <div className="text-xs uppercase tracking-wide text-zinc-500">Active bindings</div>
                    {activeBindings.map((binding) => (
                        <AeroExitSelectionBindingRow
                            binding={binding}
                            isRetiring={isRetiring}
                            key={binding.id}
                            onRetire={() => onRetireBinding(binding.id)}
                        />
                    ))}
                </div>
            )}

            <AeroExitSelectionBindingForm
                canCreate={selectedModeSupported && !isCreating}
                hasActiveBindings={activeBindings.length > 0}
                isCreating={isCreating}
                requireOptionBuyingStrategyMatch={requireOptionBuyingStrategyMatch}
                selectedModeSupported={selectedModeSupported}
                selectedTradingProfileId={selectedTradingProfileId}
                selectionMode={selectionMode}
                selectionModeOptions={selectionModeOptions}
                onCreate={createBinding}
                onRequireOptionBuyingStrategyMatchChange={setRequireOptionBuyingStrategyMatch}
                onSelectionModeChange={setSelectionMode}
                onTradingProfileChange={setSelectedTradingProfileId}
            />

            <AeroExitSelectionDecisionAudit decisions={decisions} isLoading={isDecisionLoading} />

            {retiredBindingCount > 0 && (
                <div className="text-xs text-zinc-500">
                    {retiredBindingCount} retired {retiredBindingCount === 1 ? 'binding' : 'bindings'} kept for audit.
                </div>
            )}
        </div>
    );
};

const AeroExitSelectionBindingForm = ({
    canCreate,
    hasActiveBindings,
    isCreating,
    requireOptionBuyingStrategyMatch,
    selectedModeSupported,
    selectedTradingProfileId,
    selectionMode,
    selectionModeOptions,
    onCreate,
    onRequireOptionBuyingStrategyMatchChange,
    onSelectionModeChange,
    onTradingProfileChange
}: {
    canCreate: boolean;
    hasActiveBindings: boolean;
    isCreating: boolean;
    requireOptionBuyingStrategyMatch: boolean;
    selectedModeSupported: boolean;
    selectedTradingProfileId: number | null;
    selectionMode: AeroExitSelectionMode;
    selectionModeOptions: AeroExitSelectionModeOption[];
    onCreate: () => void;
    onRequireOptionBuyingStrategyMatchChange: (value: boolean) => void;
    onSelectionModeChange: (value: AeroExitSelectionMode) => void;
    onTradingProfileChange: (value: number | null) => void;
}) => (
    <div className="space-y-3 rounded-md border border-zinc-800 bg-black/20 p-3">
        <div className="rounded-md border border-cyan-900/60 bg-cyan-950/20 p-3 text-xs text-cyan-100">
            Runtime selection only considers snapshot winners that are published as global exit candidates and attached to this strategy. A
            selected trading profile can restrict that list further.
        </div>
        <TradingProfileSelect
            noMargin
            label="Trading profile"
            placeholder="All trading profiles"
            value={selectedTradingProfileId}
            onChange={onTradingProfileChange}
        />
        <FormSelect
            noMargin
            label="Selection mode"
            options={selectionModeOptions}
            value={selectionMode}
            onChange={(event) => onSelectionModeChange(parseAeroSelectionMode(event.target.value))}
        />
        <FormCheckbox
            checked={requireOptionBuyingStrategyMatch}
            label="Match option strategy"
            labelClassName="text-zinc-200"
            onChange={(event) => onRequireOptionBuyingStrategyMatchChange(event.target.checked)}
        />
        {!selectedModeSupported && (
            <div className="text-xs text-amber-200">This job does not have the snapshot scope required by that mode.</div>
        )}
        <Button fullWidth disabled={!canCreate} isLoading={isCreating} size="sm" onClick={onCreate}>
            {hasActiveBindings ? 'Replace Binding' : 'Create Binding'}
        </Button>
    </div>
);

const AeroSnapshotReadiness = ({ snapshots }: { snapshots: AeroPointInTimeSnapshotSummary[] }) => {
    if (snapshots.length === 0) {
        return (
            <div className="rounded-md border border-amber-900/70 bg-amber-950/30 p-3 text-sm text-amber-100">
                No active point-in-time snapshots were found for this job.
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-2">
            {snapshots.map((snapshot) => (
                <div className="rounded-md border border-zinc-800 bg-black/20 p-3" key={snapshot.selectionScope}>
                    <div className="mb-2 flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-zinc-100">{formatAeroSnapshotScope(snapshot.selectionScope)}</span>
                        <StatusBadge status="active" tone="success">
                            {formatAeroCompactNumber(snapshot.activeSnapshotCount)}
                        </StatusBadge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                            <div className="text-zinc-500">Facts</div>
                            <div className="font-medium text-zinc-200">{formatAeroCompactNumber(snapshot.maxEligibleFacts)}</div>
                        </div>
                        <div>
                            <div className="text-zinc-500">Variants</div>
                            <div className="font-medium text-zinc-200">{formatAeroCompactNumber(snapshot.maxEligibleVariants)}</div>
                        </div>
                        <div className="col-span-2">
                            <div className="text-zinc-500">Latest available</div>
                            <div className="font-medium text-zinc-200">{formatUnixTimestamp(snapshot.latestAvailableAtTimestamp)}</div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

const AeroExitSelectionBindingRow = ({
    binding,
    isRetiring,
    onRetire
}: {
    binding: AeroExitSelectionBinding;
    isRetiring: boolean;
    onRetire: () => void;
}) => (
    <div className="rounded-md border border-cyan-900/60 bg-cyan-950/20 p-3">
        <div className="flex items-start justify-between gap-3">
            <div>
                <div className="text-sm font-medium text-zinc-100">{formatAeroSelectionMode(binding.selectionMode)}</div>
                <div className="mt-1 text-xs text-zinc-400">
                    {binding.tradingProfileName ?? 'All trading profiles'} • Priority {binding.priority}
                </div>
                <div className="mt-1 text-xs text-zinc-500">
                    Effective{' '}
                    {binding.effectiveFromTimestamp === 0 ? 'from first snapshot' : formatUnixTimestamp(binding.effectiveFromTimestamp)}
                </div>
            </div>
            <Button disabled={isRetiring} size="sm" variant="outline-dark" onClick={onRetire}>
                Retire
            </Button>
        </div>
    </div>
);

const AeroExitSelectionDecisionAudit = ({ decisions, isLoading }: { decisions: AeroExitSelectionDecision[]; isLoading: boolean }) => {
    if (isLoading) {
        return <DataLoadingSpinner text="Loading selection audit..." />;
    }

    if (decisions.length === 0) {
        return (
            <div className="rounded-md border border-zinc-800 bg-black/20 p-3 text-sm text-zinc-400">
                No portfolio or live decisions have used this job yet.
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="text-xs uppercase tracking-wide text-zinc-500">Latest decisions</div>
            {decisions.slice(0, 5).map((decision) => (
                <div className="rounded-md border border-zinc-800 bg-black/20 p-3" key={decision.id}>
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                        <StatusBadge status={decision.selectionStatus} tone={getAeroDecisionStatusTone(decision.selectionStatus)}>
                            {formatAeroDecisionStatus(decision.selectionStatus)}
                        </StatusBadge>
                        <span className="text-xs text-zinc-500">{formatAeroSelectionSurface(decision.selectionSurface)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                            <div className="text-zinc-500">Entity</div>
                            <div className="font-medium text-zinc-200">
                                {decision.entityType} #{decision.entityId}
                            </div>
                        </div>
                        <div>
                            <div className="text-zinc-500">Decision</div>
                            <div className="font-medium text-zinc-200">{formatUnixTimestamp(decision.decisionTimestamp)}</div>
                        </div>
                        <div>
                            <div className="text-zinc-500">Rank</div>
                            <div className="font-medium text-zinc-200">
                                {decision.selectedRankWithinScope === null ? 'None' : `#${decision.selectedRankWithinScope}`}
                            </div>
                        </div>
                        <div>
                            <div className="text-zinc-500">Score</div>
                            <div className="font-medium text-zinc-200">{formatNullableAeroNumber(decision.selectedScore)}</div>
                        </div>
                    </div>
                    {decision.rejectionReason && <div className="mt-2 text-xs text-red-200">{decision.rejectionReason}</div>}
                </div>
            ))}
        </div>
    );
};

const Metric = ({ label, value }: { label: string; value: string }) => (
    <div className="rounded-md border border-zinc-800 bg-black/20 p-3">
        <div className="text-xs uppercase tracking-wide text-zinc-500">{label}</div>
        <div className="mt-1 break-words text-sm font-medium text-zinc-100">{value}</div>
    </div>
);

/* -------------------------------------------------------------------------- */
/* Formatters                                                                 */
/* -------------------------------------------------------------------------- */

const formatUnixTimestamp = (timestamp: number | null): string => {
    if (timestamp === null || !Number.isFinite(timestamp) || timestamp <= 0) return 'None';

    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    }).format(new Date(timestamp * 1000));
};

const parseAeroSelectionMode = (value: string): AeroExitSelectionMode => {
    if (value === 'overall' || value === 'entity') return value;
    return 'entity_with_overall_fallback';
};

const formatAeroSnapshotScope = (scope: AeroPointInTimeSnapshotSummary['selectionScope']): string => {
    switch (scope) {
        case 'overall':
            return 'Overall snapshots';
        case 'entity':
            return 'Entity snapshots';
        case 'cohort':
            return 'Cohort snapshots';
        default:
            return scope;
    }
};

const formatAeroSelectionMode = (mode: AeroExitSelectionMode): string => {
    switch (mode) {
        case 'overall':
            return 'Overall winner';
        case 'entity':
            return 'Entity winner only';
        case 'entity_with_overall_fallback':
            return 'Entity winner with overall fallback';
        default:
            return mode;
    }
};

const formatAeroSelectionSurface = (surface: AeroExitSelectionDecision['selectionSurface']): string => {
    switch (surface) {
        case 'portfolio_backtest':
            return 'Portfolio backtest';
        case 'strategy_backtest':
            return 'Strategy backtest';
        case 'live_trade':
            return 'Live trade';
        case 'paper_trade':
            return 'Paper trade';
        case 'manual_trade':
            return 'Manual trade';
        case 'simulation':
            return 'Simulation';
        default:
            return surface;
    }
};

const formatAeroDecisionStatus = (status: AeroExitSelectionDecision['selectionStatus']): string => {
    switch (status) {
        case 'selected':
            return 'Selected';
        case 'fallback':
            return 'Fallback';
        case 'rejected':
            return 'Rejected';
        case 'missing_snapshot':
            return 'Missing snapshot';
        case 'invalid_snapshot':
            return 'Invalid snapshot';
        default:
            return status;
    }
};

const getAeroDecisionStatusTone = (status: AeroExitSelectionDecision['selectionStatus']): 'success' | 'warning' | 'danger' | 'neutral' => {
    switch (status) {
        case 'selected':
            return 'success';
        case 'fallback':
            return 'warning';
        case 'missing_snapshot':
        case 'invalid_snapshot':
        case 'rejected':
            return 'danger';
        default:
            return 'neutral';
    }
};

const formatNullableAeroNumber = (value: number | null): string => (value === null ? 'None' : formatAeroNumber(value));

const formatPercentList = (values: number[] | null | undefined): string => {
    if (!values || values.length === 0) return 'None';

    return `${values.map((value) => formatAeroNumber(value)).join(', ')}%`;
};

const describeAeroBaseUniverse = (job: AeroOptimizationJobDetail): string => {
    switch (job.baseUniverseSource) {
        case 'screener_candidate':
            return job.baseUniverseScreenerRuleId === null
                ? 'Screener candidate universe'
                : `Screener #${job.baseUniverseScreenerRuleId} candidates`;
        case 'resolved_screener_candidate':
            return job.baseUniverseScreenerRuleId === null
                ? 'Resolved screener candidates'
                : `Resolved screener #${job.baseUniverseScreenerRuleId} candidates`;
        case 'current_qualified_snapshot':
            return 'Current qualified snapshot';
        case 'explicit':
        case null:
            return 'Explicit entity list';
        default:
            return job.baseUniverseSource;
    }
};

const describeAeroEntryGate = (job: AeroOptimizationJobDetail): string =>
    job.entryScreenerRuleId === null ? 'No attached screener' : `Strategy screener #${job.entryScreenerRuleId}`;

const describeOptionStrategies = (job: AeroOptimizationJobDetail): string => {
    const strategies = job.optionBuyingStrategies ?? [];

    if (strategies.length === 0) return 'None';

    return strategies
        .map(
            (strategy) =>
                `${strategy.name ?? `OBS #${strategy.id}`} (#${strategy.id}${strategy.openTradeType ? `, ${strategy.openTradeType}` : ''})`
        )
        .join('; ');
};

const describeTrailing = (job: AeroOptimizationJobDetail): string => {
    const trailing = job.config.stopLoss?.trailing;

    if (!trailing) return 'Off';

    return `Activate at ${trailing.activationProfits.join(', ')}% • Trail by ${trailing.trailByPercentages.join(', ')}%`;
};

const describeStopLoss = (job: AeroOptimizationJobDetail): string => {
    const stopLoss = job.config.stopLoss;

    if (!stopLoss) return 'None';

    const basis = stopLoss.basis === 'requiredCapital' ? 'required capital' : 'opening premium';

    return `${formatPercentList(stopLoss.percentages)} of ${basis}`;
};

const describePartialExits = (job: AeroOptimizationJobDetail): string => {
    if (job.config.profitTargetAutoPartialExits) return 'Auto-generated ladders';

    const partials = job.config.profitTarget?.partialExits ?? [];

    if (partials.length === 0) return 'None';

    return partials.map((partial) => `${formatAeroNumber(partial.exitPercentage)}% at ${formatAeroNumber(partial.atProfit)}%`).join('; ');
};

const describeProfitFloors = (job: AeroOptimizationJobDetail): string => {
    const floors = job.config.profitFloor?.floors ?? [];

    if (floors.length === 0) return 'None';

    return floors
        .map((floor) => `exit to ${formatAeroNumber(floor.sellAtPercentage)}% at ${formatAeroNumber(floor.triggerPercentage)}%`)
        .join('; ');
};

const describeWindow = (job: AeroOptimizationJobDetail): string => {
    if (!job.windowStartDateTime || !job.windowEndDateTime) return 'Not recorded';

    return `${job.windowStartDateTime} → ${job.windowEndDateTime}`;
};

export default AeroDetails;
